import {
  autoUpdate,
  flip,
  offset,
  type Placement,
  shift,
  size,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from "@floating-ui/react";
import { useMemo, useState } from "react";

export interface TooltipOptions {
  initialOpen?: boolean;
  placement?: Placement;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dismissOnOutsidePress?: boolean;
}

export const useTooltip = ({
  initialOpen = false,
  placement = "top",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  dismissOnOutsidePress = true,
}: TooltipOptions = {}) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);
  const [previousControlledOpen, setPreviousControlledOpen] =
    useState(controlledOpen);
  if (controlledOpen !== previousControlledOpen) {
    setPreviousControlledOpen(controlledOpen);
    if (controlledOpen === false) setUncontrolledOpen(false);
  }

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;
  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({
        fallbackAxisSideDirection: "start",
        crossAxis: placement.includes("-"),
      }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });
  const interactions = useInteractions([
    useHover(data.context, {
      move: false,
      enabled: controlledOpen == null,
      delay: { open: 500, close: 100 },
    }),
    useFocus(data.context, { enabled: controlledOpen == null }),
    useDismiss(data.context, { outsidePress: dismissOnOutsidePress }),
    useRole(data.context, { role: "tooltip" }),
  ]);

  return useMemo(
    () => ({ open, setOpen, ...interactions, ...data }),
    [data, interactions, open, setOpen],
  );
};
